/**
 * Link handling for CliTerminal (task 101): a CLI login prints a URL, or asks the sandbox's
 * browser shim to open one, and the person must be able to act on it.
 *
 *   - Plain http(s) URLs in the output become clickable (device-code pages, auth links).
 *   - OSC 8 hyperlinks (what the shim prints) are clickable through xterm's link handler.
 *   - The shim's OSC 777 `inferrex-open;<url>` is handed to the host page, which decides how
 *     to surface it (the terminal itself only prints it).
 *
 * Pure functions, so they are tested without a DOM.
 */

/** OSC identifier and tag the browser shim emits (services/cli-sandbox/src/runtime-contract.ts). */
export const OPEN_URL_OSC = 777;
const OPEN_URL_TAG = 'inferrex-open;';

/** Only web URLs are ever opened from terminal output. */
export function isWebUrl(text: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(text);
}

export interface UrlMatch {
  /** 0-based column of the first character. */
  readonly start: number;
  /** 0-based column one past the last character. */
  readonly end: number;
  readonly url: string;
}

/**
 * Every http(s) URL in one line of terminal text. Trailing punctuation that is almost always
 * sentence-level (".", ",", a ")" with no matching "(" in the URL, quotes) is not part of it.
 */
export function findUrls(line: string): UrlMatch[] {
  const out: UrlMatch[] = [];
  for (const m of line.matchAll(/https?:\/\/[^\s"'<>`]+/gi)) {
    let url = m[0];
    const unbalancedParen = (u: string): boolean => u.endsWith(')') && u.split(')').length > u.split('(').length;
    while (/[.,;:!?'"\]]$/.test(url) || unbalancedParen(url)) url = url.slice(0, -1);
    if (url.length > 'https://'.length) out.push({ start: m.index!, end: m.index! + url.length, url });
  }
  return out;
}

/** The URL in an OSC 777 payload from the shim, or null for anything else. */
export function openUrlFromOsc(data: string): string | null {
  if (!data.startsWith(OPEN_URL_TAG)) return null;
  const url = data.slice(OPEN_URL_TAG.length);
  return isWebUrl(url) ? url : null;
}
